import type { Door, Group, Lock, Membership, Person, PersonAppStatusRow } from './types';

/**
 * Onboarding-checklist voor een nieuwe club (dashboard van platform_admin en club_admin).
 * Alles wordt afgeleid uit bestaande data; er is geen aparte "onboarding-status" in de database.
 */
export type OnboardingKey =
  | 'admin_invited'
  | 'locks_assigned'
  | 'doors_created'
  | 'locks_linked'
  | 'locks_installed'
  | 'groups_created'
  | 'members_invited';

export interface OnboardingStep {
  key: OnboardingKey;
  label: string;
  done: boolean;
  /** Korte uitleg wat er nog moet gebeuren (of wat er al is). */
  detail: string;
  /** Pad binnen de club (bijv. 'deuren') waar je dit regelt; null = Antreja regelt het. */
  path: string | null;
}

export interface OnboardingInput {
  memberships: Pick<Membership, 'profile_id' | 'role'>[];
  locks: Pick<Lock, 'id' | 'status'>[];
  doors: Pick<Door, 'id' | 'lock_id' | 'archived_at'>[];
  locationCount: number;
  groups: Pick<Group, 'id' | 'archived_at'>[];
  persons: Pick<Person, 'id' | 'profile_id' | 'archived_at'>[];
  appStatus: Pick<PersonAppStatusRow, 'person_id' | 'profile_id' | 'invite_id'>[];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function onboardingSteps(i: OnboardingInput): OnboardingStep[] {
  const admins = i.memberships.filter((m) => m.role === 'club_admin');
  // Sloten die meetellen: alles wat de club heeft, behalve defect/afgeschreven.
  const locks = i.locks.filter((l) => l.status === 'assigned' || l.status === 'installed');
  const doors = i.doors.filter((d) => !d.archived_at);
  const lockOnDoor = new Set(doors.map((d) => d.lock_id).filter(Boolean) as string[]);
  const linked = locks.filter((l) => lockOnDoor.has(l.id));
  const installed = linked.filter((l) => l.status === 'installed');
  const groups = i.groups.filter((g) => !g.archived_at);
  // Beheerders staan zelf ook als persoon in de club; die tellen niet als "leden uitgenodigd".
  const adminProfiles = new Set(i.memberships.filter((m) => m.role !== 'member').map((m) => m.profile_id));
  const status = new Map(i.appStatus.map((s) => [s.person_id, s]));
  const reached = i.persons.filter((p) => {
    if (p.archived_at) return false;
    if (p.profile_id && adminProfiles.has(p.profile_id)) return false;
    return !!p.profile_id || !!status.get(p.id)?.invite_id;
  });

  return [
    {
      key: 'admin_invited',
      label: 'Beheerder uitgenodigd',
      done: admins.length > 0,
      detail: admins.length ? plural(admins.length, 'clubbeheerder', 'clubbeheerders') : 'Nodig de contactpersoon van de club uit als clubbeheerder.',
      path: 'instellingen',
    },
    {
      key: 'locks_assigned',
      label: 'Sloten toegewezen',
      done: locks.length > 0,
      detail: locks.length ? plural(locks.length, 'slot', 'sloten') : 'Antreja wijst de gekochte sloten toe aan de club.',
      path: null,
    },
    {
      key: 'doors_created',
      label: 'Locaties en deuren aangemaakt',
      done: i.locationCount > 0 && doors.length > 0,
      detail: doors.length
        ? `${plural(i.locationCount, 'locatie', 'locaties')}, ${plural(doors.length, 'deur', 'deuren')}`
        : 'Zet per locatie de deuren klaar die een slot krijgen.',
      path: 'deuren',
    },
    {
      key: 'locks_linked',
      label: 'Sloten aan deuren gekoppeld',
      done: locks.length > 0 && linked.length === locks.length,
      detail: locks.length ? `${linked.length} van ${plural(locks.length, 'slot', 'sloten')} gekoppeld` : 'Eerst sloten toewijzen.',
      path: 'deuren',
    },
    {
      key: 'locks_installed',
      label: 'Sloten geïnstalleerd',
      done: locks.length > 0 && installed.length === locks.length,
      detail: locks.length
        ? `${installed.length} van ${plural(locks.length, 'slot', 'sloten')} geïnstalleerd met de Antreja-app`
        : 'De installateur plaatst de sloten en koppelt ze met de Antreja-app.',
      path: 'deuren',
    },
    {
      key: 'groups_created',
      label: 'Groepen aangemaakt',
      done: groups.length > 0,
      detail: groups.length ? plural(groups.length, 'groep', 'groepen') : 'Bijvoorbeeld "Barvrijwilligers" of "Trainers senioren", met deuren en tijden.',
      path: 'groepen',
    },
    {
      key: 'members_invited',
      label: 'Leden uitgenodigd',
      done: reached.length > 0,
      detail: reached.length ? plural(reached.length, 'lid heeft de app of een uitnodiging', 'leden hebben de app of een uitnodiging') : 'Nodig leden uit voor de Antreja-app.',
      path: 'personen',
    },
  ];
}

export const onboardingComplete = (steps: OnboardingStep[]) => steps.every((s) => s.done);
